import get from 'lodash/get';
import { TestStatusMap } from '../constants/test';
import { DemoTestIds } from '../constants/views';

import {
  CHROME,
  COMET,
  EDGE,
  ELECTRON,
  FIREFOX,
  INTERNET_EXPLORER,
  MOBILE,
  OPERA,
  PW_CHROMIUM,
  PW_FIREFOX,
  PW_WEBKIT,
  SAFARI,
  WEBKIT,
  YANDEX,
} from '../constants/Browsers';
import createRestApiClient from './createRestApiClient';
import { getEnv, getEnvVar } from './env';

import { EventsMap } from '../constants/events';
import { LogsType } from '../components/TestTabs/Logs/constants';

import { TestCategories, TestTypes } from '../constants/views';
import defaultBrowserIcon from '../assets/browser.svg';
import cometIcon from '../assets/comet.svg';
import appiumIcon from '../assets/appium.svg';
import chromeIcon from '../assets/browsers/chrome.svg';
import chromiumIcon from '../assets/browsers/chromium.svg';
import edgeIcon from '../assets/browsers/edge.svg';
import electonIcon from '../assets/browsers/electron.svg';
import firefoxNightlyIcon from '../assets/browsers/firefox-nightly.svg';
import firefoxIcon from '../assets/browsers/firefox.svg';
import ieIcon from '../assets/browsers/ie.svg';
import mobileIcon from '../assets/browsers/mobile.svg';
import operaIcon from '../assets/browsers/opera.svg';
import safariIcon from '../assets/browsers/safari.svg';
import webkitIcon from '../assets/browsers/webkit.svg';
import yandexIcon from '../assets/browsers/yandex.svg';
import cypressIcon from '../assets/cypress.svg';
import hypertestIcon from '../assets/hypertest.svg';
import k6Icon from '../assets/frameworks/k6.svg';
import linuxIcon from '../assets/os/Linux.svg';
import androidIcon from '../assets/os/android.svg';
import fireOsIcon from '../assets/os/fireos.svg';
import appleIcon from '../assets/os/ios.svg';
import windowsIcon from '../assets/os/windows.svg';
import playwrightIcon from '../assets/frameworks/playwright.svg';
import puppeteerIcon from '../assets/frameworks/puppeteer.svg';
import seleniumIcon from '../assets/frameworks/selenium.svg';
import taikoIcon from '../assets/frameworks/taiko.svg';

import moment from 'moment';

export const getOrdinalSuffix = (number) => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const modulo10 = number % 10;
  const modulo100 = number % 100;

  if (modulo100 >= 11 && modulo100 <= 13) {
    return suffixes[0]; // 11th, 12th, 13th, etc.
  }

  switch (modulo10) {
    case 1:
      return suffixes[1]; // 1st
    case 2:
      return suffixes[2]; // 2nd
    case 3:
      return suffixes[3]; // 3rd
    default:
      return suffixes[0]; // 4th, 5th, 6th, etc.
  }
};

export const isStage = () => {
  const hostname = window.location.hostname;
  return hostname?.startsWith('stage-') || hostname?.startsWith('dev.');
};

export const getBrandName = () => {
  const origin = window.location.origin; // For eg: https://stage-automation-infosys.lambdatestinternal.com
  const regex = new RegExp(/automation/g);

  // Check URLs with more than 5 dots from brand generation logic and ignores them as these do not
  // follow brand name convention that we are following.
  // For example: https://hyperexecute-wf-automation.cluster005.aks-scus-p.k8s.wellsfargo.net
  if (origin.match(/\./g).length >= 5) {
    return '';
  }

  if (origin.split(regex)[1]) {
    const domainParts = origin.split(regex)[1].slice(1); // 'infosys.lambdatestinternal.com'
    const desiredDomainParts = domainParts.split('.'); // ['infosys', 'lambdatestinternal', 'com']
    if (desiredDomainParts[0].includes('lambdatest')) {
      return null;
    }
    // Handles HyEx.cloud domain.
    if (
      desiredDomainParts?.length === 2 &&
      desiredDomainParts[0].includes('hyperexecute') &&
      desiredDomainParts[1].includes('cloud')
    ) {
      return null;
    }
    return desiredDomainParts[0];
  }
};

export const getLTDomainName = () => {
  const hostname = window.location.hostname;
  if (isStage()) {
    return '.lambdatestinternal.com';
  }
  return '.lambdatest.com';
};

export const getUrlByUserDomain = (
  urlPrefix,
  urlProtocol = 'https',
  isHyperExecuteCloudDomain
) => {
  const origin = window.location.origin;
  const domainParts = origin.split('.');
  // Remove the first word from the domain as it is not needed.
  domainParts.shift();

  // Remove the first word again from the remaining list if it is a enterprise URL.
  // Sample URL: https://automation.infosys.lambdatestinternal.com
  if (
    (origin.includes('lambdatest') || origin.includes('hyperexecute.cloud')) &&
    getBrandName()
  ) {
    domainParts.shift();
  }
  if (isHyperExecuteCloudDomain && isHyperExecuteCloudDomain === 'true') {
    const domainName = getLTDomainName();
    return `${urlProtocol}://${urlPrefix}${domainName}`;
  }
  const domainToAdd = `.${domainParts.join('.').split(':')[0]}`;

  return urlPrefix
    ? `${urlProtocol}://${urlPrefix}${domainToAdd}`
    : domainToAdd;
};

/**
 * Checks if the TMS Merger feature flag is enabled.
 * @returns {boolean} True if TMS Merger is enabled, false otherwise.
 */
export const isTmsMergerEnabled = () => {
  const featureFlags = window?.amplitudeData || {};
  const tmsMergerFlag = featureFlags?.tms_merger_enabled;
  return tmsMergerFlag?.variant?.key === 'true';
};

/**
 * Gets the API base URL prefix with TMS Merger suffix if enabled.
 * @returns {string} The API base URL prefix (e.g., "stage-api" or "stage-api/tms-merger")
 */
export const getApiBaseUrlPrefix = () => {
  const basePrefix = getEnvVar('REACT_APP_API_BASE_URL_PREFIX');

  if (isTmsMergerEnabled()) {
    return `${basePrefix}/tms-merger`;
  }

  return basePrefix;
};

/**
 * Gets the full API base URL with TMS Merger suffix if enabled.
 * @param {string} urlProtocol - The protocol to use (default: "https")
 * @param {string} isHyperExecuteCloudDomain - Whether this is a HyperExecute cloud domain
 * @param {boolean} excludeFromTmsMerger - Whether to skip adding the tms-merger suffix.
 *                                        Excluded APIs: LTMA (`/automation`), Insights (`/insights`).
 * @returns {string} The full API base URL
 */
export const getApiBaseUrl = (
  urlProtocol = 'https',
  isHyperExecuteCloudDomain,
  excludeFromTmsMerger = false
) => {
  const baseUrl = getUrlByUserDomain(
    getEnvVar('REACT_APP_API_BASE_URL_PREFIX'),
    urlProtocol,
    isHyperExecuteCloudDomain
  );

  if (isTmsMergerEnabled() && !excludeFromTmsMerger) {
    return `${baseUrl}/tms-merger`;
  }

  return baseUrl;
};

export function isSharingRoute() {
  const pathname = window.location.pathname;
  // Legacy magic-leap share shapes are exact paths. Kane nests the `share`
  // segment inside a longer path — `/projects/.../test-instance/.../share/<id>`
  // — so we also accept any pathname containing a `/share/` segment pair.
  // Strictly additive: every URL that used to match still matches.
  return pathname === '/share' ||
    pathname === '/share/playwright-trace' ||
    pathname === '/share/lighthouse-report' ||
    pathname.indexOf('/share/') !== -1;
}

export const isRenderingInTestDrawerFlagEnabled = (featureFlags) => {
  return (
    get(featureFlags, ['ml_test_page_in_drawer', 'variant', 'key'], 'false') ===
    'true'
  );
};

export const isHyExOnPremDomain = () => {
  const hostname = window.location.hostname;
  return (
    hostname.includes('hyperexecute.cloud') ||
    window?._env_?.REACT_APP_HYPER_EXECUTE_CLOUD_DOMAIN === 'true'
  );
};

export const isLiveRerunTest = (testDetail) => {
  const { status, parentTestDetails = {}, testResult = null } =
    testDetail || {};

  const { parentTestId = '' } = parentTestDetails || {};

  return (
    !!parentTestId &&
    !testResult &&
    status.toLowerCase() === TestStatusMap.RUNNING
  );
};

export function sendAmplitudeEvents(eventName, data) {
  const prefixedEventName = window.location.pathname.includes('/test-instance/') ? `testRunInstance: ${eventName}` : eventName;
  if (window.amplitude) {
    if (window.amplitude.track) {
      window.amplitude.track(prefixedEventName, {
        ...data,
        isNewTheme: true,
        themeVersion: 'v2',
        page: window.location.href,
        isTestRenderedInDrawer: isRenderingInTestDrawerFlagEnabled(
          window.amplitudeData
        ),
      });
    } else {
      window.amplitude.getInstance().logEvent(prefixedEventName, {
        ...data,
        isNewTheme: true,
        themeVersion: 'v2',
        page: window.location.href,
        isTestRenderedInDrawer: isRenderingInTestDrawerFlagEnabled(
          window.amplitudeData
        ),
      });
    }
  }
}

export const isNewCypressTestViewEnabled = (featureFlags) => {
  return (
    get(featureFlags, ['ml_new_cypress_test_view', 'variant', 'key'], 'off') ===
    'on'
  );
};

export const isLtCentralizedProjectFlagEnabled = (featureFlags) => {
  return (
    get(
      featureFlags,
      ['lt_centralized_projects', 'variant', 'key'],
      'false'
    ) === 'true'
  );
};

export const userLocalStorage = {
  setItem: (id, key, value) => {
    const storeValue = localStorage.getItem(key);
    try {
      const dataObject = (storeValue && JSON.parse(storeValue)) || {};
      dataObject[id] = value;
      localStorage.setItem(key, JSON.stringify(dataObject));
    } catch (e) {
      const dataObject = {};
      dataObject[id] = value;
      localStorage.setItem(key, JSON.stringify(dataObject));
    }
  },
  getItem: (id, key) => {
    try {
      const dataObject = JSON.parse(localStorage.getItem(key));
      return dataObject?.[id];
    } catch (e) {
      return false;
    }
  },
};

export const getUrlObject = () => {
  // Get the current URL
  const currentURL = window.location.href;

  // Create a URL object
  const urlObject = new URL(currentURL);

  return urlObject;
};

export const isAndroidOrIosTestRunning = (testDetails) => {
  return (
    testDetails &&
    !testDetails.isRealMobile &&
    (testDetails?.environment?.category === 'android' ||
      testDetails?.category === 'android' ||
      testDetails?.environment?.category === 'ios' ||
      testDetails?.category === 'ios') &&
    !isCDPTest(testDetails?.type) &&
    testDetails?.statusInd?.toLowerCase() === 'running'
  );
};

export const isCDPTest = (type) => {
  const env = getEnv();
  return env?.REACT_APP_CDP_TEST_TYPES?.indexOf(type) !== -1;
};

export const isHyperExecuteLiveLogsEnabled = (testDetails) => {
  return (
    testDetails?.type === TestTypes.SELENIUM &&
    testDetails?.mode === 'hypertest'
  );
};

export const isMp4VideoFlagEnabled = (featureFlags) => {
  const { ml_mp4_video_live_command_log } = featureFlags || {};

  return (
    (typeof ml_mp4_video_live_command_log === 'boolean' &&
      ml_mp4_video_live_command_log) ||
    ml_mp4_video_live_command_log === 'true'
  );
};

export const isPwAndroidLiveLogsEnabled = (featureFlags) => {
  const { pw_mobile_live_command_logs } = featureFlags || {};

  return (
    (typeof pw_mobile_live_command_logs === 'boolean' &&
      pw_mobile_live_command_logs) ||
    pw_mobile_live_command_logs === 'true'
  );
};

export const appendParamsToUrl = ({
  endPoint,
  mobile = undefined,
  orgId,
  testDetails,
  type = '',
  testType = '',
  automationLogsHost = null,
  areLogsFetchedFromLTMA = false,
  isCDP = false,
}) => {
  const env = getEnv();
  const testCreatedTimestamp = moment(testDetails.createdAt).unix();
  const isWasabiEnabled = isWasabiEnabledForCurrentOrg({ testDetails });
  const { mode = '' } = testDetails;

  // for lambda fucntion - default is = desktop/s3/us
  let logsSource = env?.REACT_APP_DEBUGGER_HOST_PREFIX;
  if (isWasabiEnabled) {
    logsSource = env?.REACT_APP_WASABI_DEBUGGER_HOST_PREFIX;
    if (mobile && !isCDP) {
      logsSource = env?.REACT_APP_MOBILE_WASABI_LOG_HOST;
    }
  } else {
    // s3
    if (mobile && !isCDP) {
      logsSource = env?.REACT_APP_MOBILE_LOG_HOST;
    }
  }

  let baseUrl = areLogsFetchedFromLTMA
    ? `${getUrlByUserDomain(
        env?.REACT_APP_API_BASE_URL_PREFIX ||
          window?._env_?.REACT_APP_API_BASE_URL_PREFIX,
        'https',
        env?.REACT_APP_HIDE_PUBLIC_LINKS
      )}/automation/api/v1/getLogs`
    : `${getUrlByUserDomain(
        logsSource,
        'https',
        env?.REACT_APP_HIDE_PUBLIC_LINKS
      )}/getLogs`;

  let paramPrefix = '?';
  if (endPoint.includes('?')) {
    paramPrefix = '&';
  }
  if (isWasabiEnabled) {
    endPoint += `${paramPrefix}orgId=${orgId}&timestamp=${testCreatedTimestamp}&isWasabiEnabled=true&testCreateTimestamp=${testDetails.createdAt}`;
  } else {
    endPoint += `${paramPrefix}orgId=${orgId}&testCreateTimestamp=${testDetails.createdAt}`;
  }

  // Real Mobile Command Logs are fetched from a different place.
  if (
    type === 'commandLogs' &&
    testType === 'realMobile' &&
    !areLogsFetchedFromLTMA
  ) {
    baseUrl = `${getUrlByUserDomain(
      env?.REACT_APP_REAL_MOBILE_DEBUGGER_HOST_PREFIX,
      'https',
      env?.REACT_APP_HIDE_PUBLIC_LINKS
    )}/getLogs`;
  }

  if (testType === 'realMobile' && areLogsFetchedFromLTMA) {
    endPoint += `&isRealMobile=true`;
  }
  // Use RD Endpoint for network logs for Android PW.
  const { category = '' } = testDetails;
  const isRealMobile = testDetails && testDetails.isRealMobile ? true : false;
  const isIosPlaywright = isIosPlaywrightTest(testDetails);

  if (
    type === 'network' &&
    isCDP &&
    isRealMobile &&
    (category === TestCategories.ANDROID || isIosPlaywright)
  ) {
    endPoint += `&isRealMobile=1&mob=${category}&testType=mobile`;
  }
  if (automationLogsHost && mode === 'hypertest' && !areLogsFetchedFromLTMA) {
    baseUrl = `${automationLogsHost}/getLogs`;
  }
  return {
    updatedApiClient: createRestApiClient().withConfig({
      baseURL: baseUrl,
    }).client,
    updatedEndPoint: endPoint,
  };
};

export const isTestADemoTest = (testId) => {
  if (testId) {
    return DemoTestIds.includes(testId);
  }
  return false;
};

export const isWasabiEnabledForCurrentOrg = ({ testDetails }) => {
  const { environment = {}, feature_flag = {}, type = '' } = testDetails;
  const isWasabiEnabled = get(feature_flag, 'wasabiEnabled', false);
  if (
    (type === 'selenium' || isCDPTest(type) || type === TestTypes.CYPRESS) &&
    // Remove Real Devices. This will be added in upcoming release.
    isWasabiEnabled
  ) {
    return true;
  }
  return false;
};

export const safeParseJwt = (jwt) => {
  try {
    return JSON.parse(Buffer.from(jwt.split('.')[1], 'base64').toString());
  } catch (e) {
    return {};
  }
};

export const getBrowserIcon = (browserName) => {
  browserName = browserName ? browserName.toLowerCase() : null;
  if (browserName === MOBILE) {
    return mobileIcon;
  } else if (browserName?.indexOf(CHROME) !== -1) {
    return chromeIcon;
  } else if (browserName?.indexOf(PW_CHROMIUM) !== -1) {
    return chromiumIcon;
  } else if (browserName?.indexOf(PW_FIREFOX) !== -1) {
    return firefoxNightlyIcon;
  } else if (browserName?.indexOf(FIREFOX) !== -1) {
    return firefoxIcon;
  } else if (browserName?.indexOf(SAFARI) !== -1) {
    return safariIcon;
  } else if (browserName?.indexOf(INTERNET_EXPLORER) !== -1) {
    return ieIcon;
  } else if (browserName?.indexOf(EDGE) !== -1) {
    return edgeIcon;
  } else if (browserName?.indexOf(OPERA) !== -1) {
    return operaIcon;
  } else if (
    browserName?.indexOf(PW_WEBKIT) !== -1 ||
    browserName?.indexOf(WEBKIT) !== -1
  ) {
    return webkitIcon;
  } else if (browserName?.indexOf(YANDEX) !== -1) {
    return yandexIcon;
  } else if (browserName?.indexOf(ELECTRON) !== -1) {
    return electonIcon;
  } else if (browserName?.indexOf(COMET) !== -1) {
    return cometIcon;
  } else {
    return defaultBrowserIcon;
  }
};

export const getBuildTypeImage = (type) => {
  let buildTypeImg;
  let buildTypeName = '';

  if (type?.includes('hypertest')) {
    buildTypeImg = hypertestIcon;
    buildTypeName = 'HyperExecute';
  } else {
    switch (type) {
      case 'selenium':
        buildTypeImg = seleniumIcon;
        buildTypeName = 'Selenium';
        break;
      case 'cypress':
        buildTypeImg = cypressIcon;
        buildTypeName = 'Cypress';
        break;
      case 'taiko':
        buildTypeImg = taikoIcon;
        buildTypeName = 'Taiko';
        break;
      case 'puppeteer':
        buildTypeImg = puppeteerIcon;
        buildTypeName = 'Puppeteer';
        break;
      case 'playwright':
        buildTypeImg = playwrightIcon;
        buildTypeName = 'Playwright';
        break;
      case 'k6':
        buildTypeImg = k6Icon;
        buildTypeName = 'K6';
        break;
      case 'appium':
        buildTypeImg = appiumIcon;
        buildTypeName = 'Appium';
        break;
      default:
    }
  }

  return { buildTypeImg, buildTypeName };
};

export const getDownloadParamsForMobileTest = ({
  isOrgInfoPresent,
  testDetail,
  urlParams,
}) => {
  const testDate = testDetail.startedAt || testDetail.createdAt || null;
  const startDate = moment.utc(testDate).format('YYYY-MM-DD');
  if (
    testDetail &&
    (testDetail.category === 'android' || testDetail.category === 'ios')
  ) {
    const downloadQueryParams = `type=mobile&startDate=${startDate}`;
    // URL params are already added which contain ORG ID.
    if (isOrgInfoPresent) {
      urlParams += `&${downloadQueryParams}`;
    } else {
      urlParams = `?orgId=${testDetail.org_id}&${downloadQueryParams}`;
    }
    return urlParams;
  }
  let paramPrefix = '?';
  if (urlParams.includes('?')) {
    paramPrefix = '&';
  }
  urlParams += `${paramPrefix}testCreateTimestamp=${moment
    .utc(testDetail.createdAt)
    .format('YYYY-MM-DD HH:MM:SS')}&orgId=${
    testDetail.org_id
  }&startDate=${startDate}`;

  if (!testDetail.isRealMobile) {
    const featureFlags = testDetail?.feature_flag || {};
    const retentionDays = featureFlags?.retention_days;
    if (retentionDays) {
      urlParams += `&dataRetentionDays=${retentionDays}`;
    }
  }
  return urlParams;
};

export const getOsIcon = (osName) => {
  const os = osName ? osName.toLowerCase() : null;
  if (!os) {
    return null;
  }
  if (os.indexOf('windows') !== -1) {
    return windowsIcon;
  } else if (
    os.indexOf('mac') !== -1 ||
    os.indexOf('macos') !== -1 ||
    os.indexOf('ios') !== -1
  ) {
    return appleIcon;
  } else if (os.indexOf('android') !== -1) {
    return androidIcon;
  } else if (os.indexOf('fireos') !== -1) {
    return fireOsIcon;
  } else if (os.indexOf('linux') !== -1 || os.indexOf('ubuntu') !== -1) {
    return linuxIcon;
  } else {
    return null;
  }
};

export const getUrlByRetention = ({
  urlSuffixToAppend,
  testDetails = {},
  url = '',
  defaultRetentionDays = null,
}) => {
  const featureFlags = testDetails?.feature_flag || {};
  const retentionDays = defaultRetentionDays || featureFlags?.retention_days;
  if (retentionDays) {
    const orgPath = getOrgPath(testDetails, true /* useTwoDigitDayFormat */);
    if (orgPath) {
      return `${url}/${retentionDays}d/${orgPath}`;
    }
    return `${url}/${retentionDays}d`;
  }
  if (urlSuffixToAppend) {
    return `${url}/${urlSuffixToAppend}`;
  }
  return `${url}`;
};

export const truncateTextFromMiddle = (textToTrim, maxLength) => {
  if (!textToTrim || textToTrim?.length <= maxLength) {
    return textToTrim;
  }
  const ellipsis = '...';
  const halfMaxLength = Math.floor((maxLength - ellipsis?.length) / 2);
  const firstHalf = textToTrim.slice(0, halfMaxLength);
  const secondHalf = textToTrim.slice(-halfMaxLength);
  return `${firstHalf}${ellipsis}${secondHalf}`;
};

export function updateAriaLive(updatedText, id = 'lt-aria-live') {
  // aria live element is added in index.html
  const ariaLiveElem = document.getElementById(id);
  if (ariaLiveElem) {
    ariaLiveElem.removeAttribute('inert');
    ariaLiveElem.innerText = updatedText || '';
  } else {
    console.error('Aria Live Element Missing');
  }
}

export const formatBytes = (bytes) => {
  const decimals = 2;
  if (bytes === 0 || bytes === -1) return '0';
  const k = 1024,
    dm = decimals <= 0 ? 0 : decimals || 2,
    sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const deleteObjectById = (array = [], key, idToDelete) => {
  const data = [...array];
  const index = data.findIndex((obj) => obj[key] === idToDelete);
  if (index !== -1) {
    data.splice(index, 1);
  }
  return data;
};

export const parseJwt = (token) => {
  //var base64Url = token.split(".")[1];

  // if(token[1]){
  //   base64Url = token[1].split(".")[1];
  // }
  let base64Url = token.split('.')[1];
  if (base64Url) {
    var base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64) || '{}');
  }
  return false;
};

export const isMp4VideoFeatureEnabled = (feature_flag) => {
  const {
    ml_mp4_video_live_command_log,
    'hye-video-live-streaming': hyp_live_video,
  } = feature_flag || {};
  return isTrue(ml_mp4_video_live_command_log) || isTrue(hyp_live_video);
};

/**
 * Parses JSON string object. Returns an empty object incase parsing fails.
 * @param {string} jsonString
 * @returns
 */
export function gracefullyParseJSON(jsonString) {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    return {};
  }
}

export const isSDKStreamEnabled = (feature_flag) => {
  return (
    get(
      feature_flag,
      ['ml_enable_new_live_stream_sdk', 'variant', 'key'],
      'false'
    ) === 'true'
  );
};

export const getOrgPath = (testDetails, useTwoDigitDayFormat = false) => {
  if (testDetails) {
    const testCreatedDate =
      get(testDetails, 'createdAt', '') ||
      get(testDetails, ['environment', 'createdAt'], '');
    const orgId =
      get(testDetails, 'org_id', 0) ||
      get(testDetails, ['environment', 'org_id'], 0);
    const dateFormat = useTwoDigitDayFormat ? 'YYYY/MM/DD' : 'YYYY/MM/D';
    const testDate = moment.utc(testCreatedDate).format(dateFormat);
    return `orgId-${orgId}/${testDate}`;
  }
  return '';
};

export function transformName(
  str,
  lengthOfStartingCharacters,
  numOfDots,
  lengthOfEndingCharacters
) {
  if (str?.length <= lengthOfStartingCharacters + lengthOfEndingCharacters) {
    return str;
  } else {
    let newStr = '';
    for (let i = 0; i < lengthOfStartingCharacters; i++) {
      newStr = newStr + str[i];
    }
    for (let i = 0; i < numOfDots; i++) {
      newStr = newStr + '.';
    }
    for (let i = str.length - lengthOfEndingCharacters; i < str.length; i++) {
      newStr = newStr + str[i];
    }
    return newStr;
  }
}

export const deriveAction = (action) => {
  let session_key = getKey(action, 'session');
  let result = [];
  action.map((item, index) => {
    // push all items to result array when session key is not present
    if (!session_key) {
      result.push(item);
    } else if (index > session_key) {
      result.push(item);
    }
    return true;
  });

  let result_string = null;
  if (result?.length > 0) {
    if (result?.length >= 3) {
      let id = result[2].split('-');
      result_string = `${result[1]}/${result[2]}`;
      if (result[3]) {
        result_string = `${result[1]}/${result[2]}/${result[3]}`;
      }
    } else {
      if (result?.length === 2) {
        result_string = result[1];
      } else {
        // for cases where session_id is not present in the action
        if (!session_key && result?.length === 1) {
          result_string = result[0];
        } else {
          result_string = null;
        }
      }
    }
  } else {
    result_string = null;
  }
  return result_string;
};

export const getBucketByRegion = (
  dataLocalisationMapping,
  currentTestRegion,
  assetType
) => {
  try {
    if (!dataLocalisationMapping) {
      return '';
    }
    if (!currentTestRegion || dataLocalisationMapping?.length === 0) {
      return '';
    }
    const regionConfig = dataLocalisationMapping.find(
      (regionInfo) => regionInfo.region === currentTestRegion
    );
    if (!regionConfig) {
      return '';
    }

    const bucketInfoList = regionConfig['meta']['real-device']['artefacts'];
    const bucketInfoForType = bucketInfoList.find(
      (bucketInfo) => bucketInfo.type === assetType
    );
    return bucketInfoForType.cfDistribution;
  } catch {
    return '';
  }
};

export const getBucketUrlForRealDevice = ({
  assetType = 'video',
  dataLocalisation = [],
  fallbackEndpoint = {},
  testRegion,
  hidePublicLinks = false,
  isWasabiEnabled = false,
}) => {
  const url = getBucketByRegion(dataLocalisation, testRegion, assetType);
  if (url) {
    return url;
  }

  if (isWasabiEnabled) {
    const env = getEnv();
    const wasabiUrl = getUrlByUserDomain(
      env?.REACT_APP_REAL_MOBILE_R2_CDN,
      'https',
      env?.REACT_APP_HIDE_PUBLIC_LINKS
    );
    if (wasabiUrl) {
      return wasabiUrl;
    }
  }

  const realMobileArtefactsUrl = getUrlByUserDomain(
    fallbackEndpoint,
    'https',
    hidePublicLinks
  );
  return realMobileArtefactsUrl;
};

export const getCookieByDomain = () => {
  const hostname = window.location.hostname;
  let tokenName = 'accessToken';
  if (
    hostname?.startsWith('stage-') ||
    hostname.includes('dev') ||
    hostname?.startsWith('ui-components-playground')
  ) {
    tokenName = 'stageAccessToken';
  }
  const allCookies = `; ${document.cookie}`;
  const parts = allCookies.split(`; ${tokenName}=`);
  if (parts?.length === 2) {
    return parts
      .pop()
      .split(';')
      .shift();
  }
};

export const isIosPlaywrightTest = (testDetails) => {
  const isPlaywright = testDetails.type === TestTypes.PLAYWRIGHT;
  const isIos = testDetails.category === TestCategories.IOS;
  return isPlaywright && isIos;
};

export const getKey = (obj, val) =>
  Object.keys(obj).find((key) => obj[key] === val);

export const isVirtualDeviceMp4VideoFlagEnabled = (featureFlags) => {
  const { virtual_device_mp4_video } = featureFlags || {};

  return (
    (typeof virtual_device_mp4_video === 'boolean' &&
      virtual_device_mp4_video) ||
    virtual_device_mp4_video === 'true'
  );
};

/**
 * This method is used to download selenium/appium, console and terminal logs
 */
export const downloadLogs = (logs, logType, testId) => {
  sendAmplitudeEvents(EventsMap.TEST_VIEW_DOWNLOAD_LOGS, {
    logsTab: logType,
  });
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.style = 'display: none';

  let blob;
  let extension = 'log';
  if (logType === LogsType.DEVICE_LOGS) {
    blob = new Blob([logs], { type: 'application/zip' });
    extension = 'zip';
  } else {
    //if logs is already a string, we don't need to stringify it again
    // stringify(logs, null, 4): pretty print the JSON with 4 spaces indentation for each level
    const formattedLogs =
      typeof logs === 'string' ? logs : JSON.stringify(logs, null, 2);

    // Replace \n with actual newline characters
    let logsWithLineBreaks = formattedLogs.replace(/\\n/g, '\n');

    // Replace \t with actual tab characters.
    logsWithLineBreaks = formattedLogs.replace(/\\t/g, '\t');

    blob = new Blob([logsWithLineBreaks], {
      type: 'octet/stream',
    });
  }
  const url = window.URL.createObjectURL(blob);
  a.href = url;
  a.target = '__blank';
  a.download = `lambda-logs-${logType}-${testId}.${extension}`;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const isElementInViewport = (elem) => {
  if (elem) {
    const rect = elem.getBoundingClientRect();
    if (
      rect.x + rect.width < 0 ||
      rect.y + rect.height < 0 ||
      rect.x > window.innerWidth ||
      rect.y > window.innerHeight
    ) {
      return false;
    }
  }
  return true;
};

export const getAndroidLiveVideoUrl = (testId) => {
  const env = getEnv();
  const androidVideoUrl = getUrlByUserDomain(
    env?.REACT_APP_ANDROID_LIVE_VIDEO,
    'https',
    env?.REACT_APP_HIDE_PUBLIC_LINKS
  );
  return `${androidVideoUrl}/?testid=${testId}${env?.REACT_APP_IFRAME_URL_APPEND ||
    ''}&q=${window.location.host}`;
};

export const isLiveInteractionEnabledForHyEx = feature_flag => {
  const { 'hye-live-interaction-enabled': hye_live_interaction_enabled } = feature_flag || {};
  return isTrue(hye_live_interaction_enabled);
};

export const isLiveEnabledForHyEx = (feature_flag) => {
  const { 'hye-video-live-streaming': hyp_live_video } = feature_flag || {};
  return isTrue(hyp_live_video);
};

export const isJsonString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

/*
 * Checks whether user ran tests in headless mode.
 * @param {!Object<string, *> parsedCapability Test capabilities
 * @return {bool} Whether headless mode was selected.
 */
export const isHeadlessMode = (parsedCapability) => {
  const desiredCapabilities =
    parsedCapability && parsedCapability.desiredCapabilities;

  if (desiredCapabilities) {
    const normalHeadlessCapability = desiredCapabilities.headless;

    const headlessInChrome = get(desiredCapabilities, 'goog:chromeOptions', {});

    const headlessInFirefox = get(
      desiredCapabilities,
      'moz:firefoxOptions',
      {}
    );

    const headlessInEdge = get(desiredCapabilities, 'ms:edgeOptions', {});

    const isHeadlessBrowserCapability = [
      headlessInChrome,
      headlessInFirefox,
      headlessInEdge,
    ].some(
      (capability) =>
        capability.args &&
        capability.args.find((value) => /headless/.test(value))
    );

    return isHeadlessBrowserCapability || normalHeadlessCapability;
  } else {
    return false;
  }
};

// this bypasses the webpack console func removal
export const debugLogger = (() => {
  return {
    info: (...args) => {
      if (window.enableDebugLogs) {
        console.log(...args);
      }
    },
    error: (...args) => {
      if (window.enableDebugLogs) {
        console.error(...args);
      }
    },
    warn: (...args) => {
      if (window.enableDebugLogs) {
        console.warn(...args);
      }
    },
  };
})();

export const isTrue = (data) => {
  return (typeof data === 'boolean' && data) || data === 'true';
};

export const getQueryStringValue = (key) => {
  return decodeURIComponent(
    window.location.search.replace(
      new RegExp(
        '^(?:.*[&\\?]' +
          encodeURIComponent(key).replace(/[\.\+\*]/g, '\\$&') +
          '(?:\\=([^&]*))?)?.*$',
        'i'
      ),
      '$1'
    )
  );
};

export const isNewAnnotationFlowEnabled = (testDetails) => {
  return (
    isTrue(
      get(testDetails?.feature_flag, ['ml_new_annotation_command_logic'], false)
    ) &&
    testDetails.type !== TestTypes.CYPRESS &&
    testDetails?.annotations &&
    testDetails?.annotations?.length
  );
};

export const hasAnnotationsData = (annotations) => {
  if (!annotations) {
    return false;
  }
  if (Array.isArray(annotations)) {
    return annotations.length > 0;
  }
  if (typeof annotations === 'object') {
    return Object.keys(annotations).length > 0;
  }
  return false;
};

export const getFFPayload = (flag) => {
  const variant = flag?.variant;
  if (!variant?.key || variant.key === 'false' || variant.key === 'off') return null;
  return variant.payload ?? null;
};

export const isTunnelActivated = (testDetails, tunnelCapability) => {
  if (!testDetails) return null;

  if (isCDPTest(testDetails.type) && testDetails.mode === "hypertest") {
    return tunnelCapability ? true : null;
  }

  return get(testDetails, 'tunnelIdentifier', null) &&
    (tunnelCapability || testDetails.type === 'cypress')
      ? testDetails.tunnelIdentifier !== '0'
      : null;
};
